import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import ProtectedLink from "../../components/ProtectedLink";

const MONTHS_FR = ['Janv','Févr','Mars','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];

function TrendIcon({ trend }) {
  if (trend === 'positive') return <span style={{ color: "#16a34a", fontSize: 14 }}>↗</span>;
  if (trend === 'negative') return <span style={{ color: "#dc2626", fontSize: 14 }}>↘</span>;
  return <span style={{ color: "#9ca3af", fontSize: 14 }}>→</span>;
}

export default function TrackingTab({ scenarioId, scenario, currentCompanyId }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [period, setPeriod]     = useState("ytd");
  const [month, setMonth]       = useState(new Date().getMonth());
  const [quarter, setQuarter]   = useState(0);
  const [msg, setMsg]           = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());

  const load = useCallback(() => {
    if (!scenarioId) return;
    setLoading(true);
    const params = { period, month: period === 'quarter' ? quarter : month };
    api.budget.comparison(scenarioId, params)
      .then(r => { setRows(r.rows || []); setCurrentMonth(r.currentMonth ?? new Date().getMonth()); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [scenarioId, period, month, quarter]);

  useEffect(() => { load(); }, [load]);

  const syncActuals = async () => {
    setSyncing(true);
    try {
      const r = await api.budget.syncActuals(scenarioId, { company_id: currentCompanyId });
      setMsg("✓ " + r.message);
      load();
    } catch (e) { setMsg("Erreur : " + e.message); }
    finally { setSyncing(false); setTimeout(() => setMsg(""), 4000); }
  };

  const fmt  = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtP = (n) => `${n > 0 ? "+" : ""}${(n || 0).toFixed(1)}%`;

  const rowColor = (row) => {
    if (Math.abs(row.ecartPct) < 5) return "transparent";
    if (row.trend === 'positive') return "#f0fdf4";
    return "#fef2f2";
  };
  const ecartColor = (row) => {
    if (Math.abs(row.ecartPct) < 5) return "#6b7280";
    return row.trend === 'positive' ? "#16a34a" : "#dc2626";
  };

  // Subtotals by category
  const subtotals = {};
  for (const row of rows) {
    if (!subtotals[row.category]) subtotals[row.category] = { budget: 0, realise: 0, ecart: 0 };
    subtotals[row.category].budget  += row.budget  || 0;
    subtotals[row.category].realise += row.realise || 0;
    subtotals[row.category].ecart   += row.ecart   || 0;
  }

  const inputStyle = { padding: "6px 10px", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 8, fontSize: 12, background: "var(--input-bg,#fff)", color: "var(--text,#1f2937)" };

  if (!scenarioId) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)", fontSize: 13 }}>Sélectionnez un scénario</div>;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select style={inputStyle} value={period} onChange={e => setPeriod(e.target.value)}>
          <option value="ytd">Cumulé YTD (jusqu'à aujourd'hui)</option>
          <option value="annual">Annuel complet</option>
          <option value="month">Par mois</option>
          <option value="quarter">Par trimestre</option>
        </select>
        {period === 'month' && (
          <select style={inputStyle} value={month} onChange={e => setMonth(parseInt(e.target.value))}>
            {MONTHS_FR.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
        )}
        {period === 'quarter' && (
          <select style={inputStyle} value={quarter} onChange={e => setQuarter(parseInt(e.target.value))}>
            <option value="0">T1 (Jan–Mar)</option>
            <option value="1">T2 (Avr–Jun)</option>
            <option value="2">T3 (Jul–Sep)</option>
            <option value="3">T4 (Oct–Déc)</option>
          </select>
        )}
        <div style={{ flex: 1 }} />
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{msg}</span>}
        <button onClick={syncActuals} disabled={syncing} style={{ padding: "7px 14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {syncing ? "Synchronisation..." : "🔄 Actualiser les réalisés"}
        </button>
        <ProtectedLink url={api.budget.exportComparisonUrl(scenarioId)} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, textDecoration: "none", color: "var(--text,#1f2937)" }}>
          ⬇ Excel
        </ProtectedLink>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement...</div>
      ) : (
        <div style={{ background: "var(--card-bg,#fff)", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Compte","Désignation","Budget","Réalisé","Écart €","Écart %","Tendance"].map(h => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: h === "Compte" || h === "Désignation" ? "left" : "right", fontWeight: 600, color: "#6b7280", fontSize: 11, borderBottom: "1px solid #e5e7eb" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(subtotals).map(([cat, st]) => {
                const catRows = rows.filter(r => r.category === cat);
                if (!catRows.length) return null;
                return [
                  <tr key={`cat-${cat}`} style={{ background: "#f9fafb", borderTop: "2px solid #e5e7eb" }}>
                    <td colSpan={2} style={{ padding: "7px 12px", fontWeight: 700, fontSize: 12, color: "#374151" }}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}s
                    </td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, fontSize: 12 }}>{fmt(st.budget)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, fontSize: 12 }}>{fmt(st.realise)}</td>
                    <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, fontSize: 12, color: st.ecart >= 0 ? "#16a34a" : "#dc2626" }}>
                      {st.ecart >= 0 ? "+" : ""}{fmt(st.ecart)}
                    </td>
                    <td colSpan={2} />
                  </tr>,
                  ...catRows.map(row => (
                    <tr key={row.id} style={{ background: rowColor(row), borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}
                      onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}>
                      <td style={{ padding: "8px 12px", color: "#6b7280", fontSize: 11 }}>{row.account_id}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <div style={{ fontWeight: 500 }}>{row.account_name}</div>
                        {row.subcategory && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{row.subcategory}</div>}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmt(row.budget)}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: row.realise > 0 ? 500 : 400, color: row.realise > 0 ? "var(--text,#1f2937)" : "#9ca3af" }}>
                        {row.realise > 0 ? fmt(row.realise) : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: ecartColor(row), fontWeight: Math.abs(row.ecartPct) >= 10 ? 700 : 400 }}>
                        {row.ecart !== 0 ? `${row.ecart >= 0 ? "+" : ""}${fmt(row.ecart)}` : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", color: ecartColor(row), fontWeight: Math.abs(row.ecartPct) >= 10 ? 700 : 400 }}>
                        {row.budget > 0 ? fmtP(row.ecartPct) : "—"}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}><TrendIcon trend={row.trend} /></td>
                    </tr>
                  )),
                ];
              })}

              {/* Global result */}
              {(() => {
                const totalBudget  = rows.filter(r => r.category === 'produit').reduce((s, r) => s + r.budget, 0) - rows.filter(r => r.category === 'charge').reduce((s, r) => s + r.budget, 0);
                const totalRealise = rows.filter(r => r.category === 'produit').reduce((s, r) => s + r.realise, 0) - rows.filter(r => r.category === 'charge').reduce((s, r) => s + r.realise, 0);
                const ecart = totalRealise - totalBudget;
                return (
                  <tr style={{ background: "#1a9e7215", borderTop: "3px solid #1a9e72" }}>
                    <td colSpan={2} style={{ padding: "10px 12px", fontWeight: 700, color: "#1a9e72", fontSize: 14 }}>RÉSULTAT NET</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, fontSize: 14 }}>{fmt(totalBudget)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, fontSize: 14 }}>{fmt(totalRealise)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, fontSize: 14, color: ecart >= 0 ? "#16a34a" : "#dc2626" }}>{ecart >= 0 ? "+" : ""}{fmt(ecart)}</td>
                    <td colSpan={2} />
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
